import { useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch, useSelector } from "../../redux-store";
import {
  selectFormDataForWhenOpeningModal,
  selectIsSaving,
  selectResetFormData,
} from "../../ducks/app/client-cargo-template-dialog/selectors";
import * as clientCargoTemplateDialogActions from "../../ducks/app/client-cargo-template-dialog";
import CreateEditShipmentDialog from "./CreateEditShipmentDialog";
import { useStableNavigate } from "../StableNavigateContext";
import { CargoFormData } from "../../ducks/app/cargo-dialog/types";
import { loadProducts } from "../../ducks/data/products";
import { CargoModalEntityType } from "./CreateEditShipmentDialog/CargoModalEntityType";
import ClientCargoTemplateSelectNameDialog from "./CreateEditShipmentDialog/components/ClientCargoTemplateSelectNameDialog";
import { CargoTemplateData } from "../../ducks/app/schedule-cargo-template-dialog/types";
import { loadCargoTemplate } from "../../ducks/app/client-cargo-template-dialog";
import { useLocation } from "react-router-dom";
import { clientCargoTemplateRemoved } from "../../ducks/data/client-order-templates";

type EditShipmentTemplateProps = {
  onClose: (props: {
    formData?: CargoFormData;
    cargoTemplateId?: string;
  }) => void;
  cargoTemplateId?: string;
};

const AddEditClientCargoTemplateModalWrapper = (
  props: EditShipmentTemplateProps
) => {
  const { cargoTemplateId } = props;
  const [showPickNameDialog, setShowPickNameDialog] = useState(false);
  const createReturnPromise = useRef<{
    resolve: Function;
    reject: Function;
  } | null>(null);
  const [cargoTemplateData, setCargoTemplateData] =
    useState<CargoTemplateData | null>(null);
  const resetData = useSelector(selectResetFormData);
  const isSaving = useSelector(selectIsSaving);
  const dispatch = useAppDispatch();
  const formStartingData = useSelector(selectFormDataForWhenOpeningModal);
  const navigate = useStableNavigate();
  const tempFormData = useRef<any>(null);
  // The name picked for a create that came back refused for lines whose CO2
  // type was deleted: the resend after the user agrees reuses it rather than
  // asking for the name again.
  const pickedName = useRef<string | null>(null);

  const onClose = ({
    formData,
    cargoTemplateId,
  }: {
    formData?: CargoFormData;
    cargoTemplateId?: string;
  }) => {
    setCargoTemplateData(null);
    props.onClose({ formData, cargoTemplateId });
  };

  useEffect(() => {
    if (cargoTemplateId) {
      dispatch(loadCargoTemplate(cargoTemplateId)).then((result) => {
        if (result.payload) {
          const data = (result.payload as any).data as CargoTemplateData; // TODO: can't infer correct type
          setCargoTemplateData(data);
        }
      });
    }
  }, [cargoTemplateId, dispatch]);

  const handleSubmit = async (
    formData: CargoTemplateData & {
      keepAlive?: boolean;
      submitType: "CREATE" | "UPDATE" | "DUPLICATE" | "CREATE_AND_MAKE_COPY";
      cargoIdToCopyInvoiceLinesFrom: string | null;
      cargoIdToCopyFilesFrom: string | null;
      dropDeletedExtraCo2Types?: boolean;
    }
  ) => {
    if (
      formData.submitType === "CREATE_AND_MAKE_COPY" ||
      formData.submitType === "DUPLICATE"
    ) {
      console.error(`Not implemented ${formData.submitType}`);
      return;
    }
    if (
      formData.submitType === "CREATE" &&
      formData.dropDeletedExtraCo2Types &&
      pickedName.current
    ) {
      return createWithName(formData, pickedName.current);
    }
    if (formData.submitType === "CREATE") {
      tempFormData.current = formData;
      setShowPickNameDialog(true);
      let resolve: Function;
      let reject: Function;
      return new Promise((res, rej) => {
        resolve = res;
        reject = rej;
        createReturnPromise.current = { resolve, reject };
      });
    }
    const res = await dispatch(
      clientCargoTemplateDialogActions.submit({
        ...formData,
        cargoTemplateId: cargoTemplateId,
        submitType: formData.submitType as "UPDATE",
      })
    ).unwrap();
    // Refused (e.g. deleted extra CO2 types, or someone else saved it
    // meanwhile): stay open, the dialog asks or handles it.
    if ("error" in res && res.error) {
      return res;
    }
    if (!res.keepAlive) {
      setCargoTemplateData(null);
      navigate({ hash: "" });
    }

    return res;
  };

  const onNamePickedForCreate = async (
    formData: CargoTemplateData & {
      keepAlive?: boolean;
      submitType: "CREATE" | "UPDATE" | "DUPLICATE" | "CREATE_AND_MAKE_COPY";
      cargoIdToCopyInvoiceLinesFrom: string | null;
      cargoIdToCopyFilesFrom: string | null;
    },
    name: string
  ) => {
    setShowPickNameDialog(false);
    pickedName.current = name;
    try {
      createReturnPromise.current?.resolve(
        await createWithName(formData, name)
      );
    } catch (e) {
      // The submit thunk has already reported it and shown the error toast.
      // Reject the pending create so the dialog's submit fails like a cancel
      // instead of waiting forever.
      createReturnPromise.current?.reject(e);
    }
    createReturnPromise.current = null;
    tempFormData.current = null;
  };

  const createWithName = async (
    formData: CargoTemplateData & {
      keepAlive?: boolean;
      submitType: "CREATE" | "UPDATE" | "DUPLICATE" | "CREATE_AND_MAKE_COPY";
      cargoIdToCopyInvoiceLinesFrom: string | null;
      cargoIdToCopyFilesFrom: string | null;
      dropDeletedExtraCo2Types?: boolean;
    },
    name: string
  ) => {
    const res = await dispatch(
      clientCargoTemplateDialogActions.submit({
        ...formData,
        submitType: formData.submitType as "CREATE",
        name,
      })
    ).unwrap();
    if ("error" in res) {
      // refused, the dialog asks and resubmits with the same name
      return res;
    }
    pickedName.current = null;
    navigate({ hash: `clientId=${clientId}&clientCargoTemplate=${res.id}` });
    return undefined;
  };

  const onCancelNamePickedForCreate = () => {
    setShowPickNameDialog(false);
    createReturnPromise.current?.reject();
    createReturnPromise.current = null;
    tempFormData.current = null;
  };

  const handleDelete = async () => {
    if (!cargoTemplateId) {
      console.error("No cargo template id to delete");
      return;
    }
    const result = await dispatch(
      clientCargoTemplateDialogActions.deleteCargoTemplate({
        cargoTemplateId,
      })
    );
    // The delete here goes through a different duck than the customer-page
    // template list (data/client-order-templates), so that list's cache
    // needs an explicit nudge to drop the deleted template too.
    if (
      clientCargoTemplateDialogActions.deleteCargoTemplate.fulfilled.match(
        result
      )
    ) {
      dispatch(clientCargoTemplateRemoved({ id: cargoTemplateId }));
    }
    // TODO: only navigate and close on success, how to do this?
    // TODO: consider moving into the redux actions layer
    navigate({ hash: "" }, { replace: true });
  };

  const [productsLoaded, setProductsLoaded] = useState(false);
  useEffect(() => {
    if (productsLoaded) {
      return;
    }
    dispatch(loadProducts())
      .unwrap()
      .then(() => setProductsLoaded(true))
      // loadProducts has already reported the failure and shown the toast;
      // without this the rejection escapes as an unhandled one (DISPATCHER-4).
      .catch(() => {});
  }, [dispatch, productsLoaded]);

  const location = useLocation();
  const params = new URLSearchParams(location.hash);
  const clientId = params.get("clientId") || undefined;

  const shouldRender = useMemo(() => {
    return (
      productsLoaded &&
      (cargoTemplateData || (!cargoTemplateId && formStartingData))
    );
  }, [productsLoaded, cargoTemplateData, formStartingData, cargoTemplateId]);

  return shouldRender ? (
    <>
      {showPickNameDialog && (
        <ClientCargoTemplateSelectNameDialog
          onClose={onCancelNamePickedForCreate}
          onSubmit={(name) => onNamePickedForCreate(tempFormData.current, name)}
          clientId={tempFormData.current.clientId}
        />
      )}
      <CreateEditShipmentDialog
        resetData={{
          ...resetData!,
          clientId,
        }}
        initialData={{
          ...(cargoTemplateData || formStartingData!),
          clientId,
        }}
        onSubmit={handleSubmit}
        onDelete={handleDelete}
        onClose={onClose}
        isSaving={isSaving}
        createNew={!cargoTemplateId}
        entityType={CargoModalEntityType.CLIENT_CARGO_TEMPLATE}
        clientIdForTemplate={clientId}
      />
    </>
  ) : null;
};

export default AddEditClientCargoTemplateModalWrapper;
